import { useMemo } from 'react';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';

import {
  ENABLE_BLF_PRODUCT,
  ENABLE_BLR_SPECIFIC_CONTENT,
  ENABLE_SCORP_BUNDLE_V1,
  RELEASE_SQUARESPACE_DOMAINS_RESELLER
} from '#client/constants/experimentsFlags';
import { PRODUCT_SERVICE_NAME } from '#client/pages/Home/constants';

/**
 * Maps product service codes to their corresponding feature flag constants.
 * Products not in this map are always shown regardless of feature flag state.
 * Products in this map are only shown when their corresponding feature flag is enabled.
 *
 * To add a new feature-flagged product:
 * 1. Add the serviceCode as a key with the feature flag constant as the value
 * 2. Add the feature flag constant to experimentsFlags.js
 * 3. Update useProductCardFeatureFlags() to include the new flag
 * 4. If the new flag also replaces an existing card (e.g. the new variant
 *    supersedes a legacy serviceCode), add the legacy serviceCode to
 *    PRODUCT_CARD_DISABLED_FEATURE_FLAGS under the same flag so the legacy
 *    card is hidden when the flag is enabled.
 *
 * @type {Record<string, string>}
 * @example
 * // Product with serviceCode 'business_license_filing' requires ENABLE_BLF_PRODUCT flag
 * PRODUCT_CARD_FEATURE_FLAGS.business_license_filing === ENABLE_BLF_PRODUCT
 */
export const PRODUCT_CARD_FEATURE_FLAGS = {
  business_license_filing: ENABLE_BLF_PRODUCT,
  s_corp_bundle: ENABLE_SCORP_BUNDLE_V1,
  [PRODUCT_SERVICE_NAME.DOMAIN_NAME_REG_SQSP]: RELEASE_SQUARESPACE_DOMAINS_RESELLER
};

/**
 * Maps product service codes that should be hidden when their feature flag is enabled
 * (the inverse of PRODUCT_CARD_FEATURE_FLAGS). Used for cards that are being replaced
 * by a newer variant gated behind the same flag, so the legacy card disappears at the
 * same moment the new one appears.
 *
 * @type {Record<string, string>}
 * @example
 * // 'domain_name_reg' is shown only while RELEASE_SQUARESPACE_DOMAINS_RESELLER is false;
 * // once the flag is enabled, 'domain_name_reg_sqsp' takes its place.
 */
export const PRODUCT_CARD_DISABLED_FEATURE_FLAGS = {
  [PRODUCT_SERVICE_NAME.DOMAIN_NAME_REG]: RELEASE_SQUARESPACE_DOMAINS_RESELLER
};

/**
 * Hook to retrieve the feature flag values for the product cards
 * @returns {object} An object with the feature flag values for the product cards
 */
export function useProductCardFeatureFlags() {
  const enableBlfProduct = useExperimentFlag(ENABLE_BLF_PRODUCT, false);
  const enableBlrSpecificContent = useExperimentFlag(ENABLE_BLR_SPECIFIC_CONTENT, false);
  const enableSCorpBundleV1 = useExperimentFlag(ENABLE_SCORP_BUNDLE_V1, false);
  const releaseSquarespaceDomainsReseller = useExperimentFlag(
    RELEASE_SQUARESPACE_DOMAINS_RESELLER,
    false
  );

  return useMemo(() => ({
    [ENABLE_BLF_PRODUCT]: enableBlfProduct,
    [ENABLE_BLR_SPECIFIC_CONTENT]: enableBlrSpecificContent,
    [ENABLE_SCORP_BUNDLE_V1]: enableSCorpBundleV1,
    [RELEASE_SQUARESPACE_DOMAINS_RESELLER]: releaseSquarespaceDomainsReseller
  }), [
    enableBlfProduct,
    enableBlrSpecificContent,
    enableSCorpBundleV1,
    releaseSquarespaceDomainsReseller
  ]);
}

/**
 * Function to filter the product cards by the feature flag values
 * @param {object[]} cards - The product cards
 * @param {object} featureFlagValues - The feature flag values
 * @returns {object[]} The filtered product cards
 */
export function filterProductCardsByFeatureFlags(cards, featureFlagValues) {
  if (!cards || !Array.isArray(cards)) {
    return [];
  }

  return cards.filter((card) => {
    if (!card || !card.serviceCode) {
      return true;
    }

    const requiredFlag = PRODUCT_CARD_FEATURE_FLAGS[card.serviceCode];
    if (requiredFlag && featureFlagValues[requiredFlag] !== true) {
      return false;
    }

    const disabledFlag = PRODUCT_CARD_DISABLED_FEATURE_FLAGS[card.serviceCode];
    if (disabledFlag && featureFlagValues[disabledFlag] === true) {
      return false;
    }

    return true;
  });
}
